/**
 * Wire schemas for the global "Find any case" search (#434) — the top-strip box
 * reachable from every Command Center screen.
 *
 * Two payloads:
 *   - `SearchResponse` — the lightweight hit list the dropdown renders. Each row
 *     carries the matter's `status`, which is the "which book is it in" answer
 *     (a Rejected matter reads "Rejected", etc.) without a status→book resolver.
 *   - `CaseLookupResponse` — the enriched by-id read behind the result-click
 *     panel: a faithful subset of the case-detail superset (identity + contact +
 *     summary + the full routing trail), assembled from LeadReader +
 *     PlacementReader + FirmReader. Settlement / filing-deadline / monthly-update
 *     sections are deferred — the panel renders only the sections present.
 */

import { z } from "zod";

/** One global-search hit — the row the dropdown renders. */
export const SearchResultSchema = z.object({
  /** The lead's Airtable record id — used to open the case panel. */
  id: z.string(),
  /** Stable human-readable Lead ID. */
  leadId: z.string(),
  /** Client / matter name. */
  name: z.string(),
  /** Lead Status — the "which book is it in" cue, or null. */
  status: z.string().nullable(),
  /** Case-type label, or null. */
  caseType: z.string().nullable(),
  /** Joined Originating Firm name (disambiguates same-named clients), or null. */
  originatingFirmName: z.string().nullable(),
});
export type SearchResult = z.infer<typeof SearchResultSchema>;

/** Payload for `GET /api/search?q=`. */
export const SearchResponseSchema = z.object({
  results: z.array(SearchResultSchema),
});
export type SearchResponse = z.infer<typeof SearchResponseSchema>;

/** One firm's leg on the matter's routing journey — the firm trail the lookup
 *  panel shows (e.g. Protection — Declined → Coastline — Declined). */
export const CaseLookupRoutingLegSchema = z.object({
  firmName: z.string().nullable(),
  decision: z.string().nullable(),
  dateSent: z.string().nullable(),
  declineReason: z.string().nullable(),
});

/** Payload for `GET /api/case/:id`. */
export const CaseLookupResponseSchema = z.object({
  /** The lead's Airtable record id (the `:id` echoed back) — lets the panel
   *  key lead-scoped sections (firm messages, #542) off a search-opened case. */
  leadRecordId: z.string(),
  name: z.string(),
  status: z.string().nullable(),
  /** TRUE for a Transfer (already-retained client). */
  signed: z.boolean(),
  caseType: z.string().nullable(),
  incidentState: z.string().nullable(),
  originatingFirmName: z.string().nullable(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  caseSummary: z.string().nullable(),
  /** Every firm the matter was routed to, with its decision — newest first. */
  routing: z.array(CaseLookupRoutingLegSchema),
});
export type CaseLookupResponse = z.infer<typeof CaseLookupResponseSchema>;

/**
 * Name folding for staff search (#879). A query typed without accents must find a name stored
 * with them: each [from, to] pair maps one character to its comparison form, applied after
 * lowercasing to the stored name and to the query alike, so every match before still matches.
 * Latin-1 accented lowercase letters fold to their base letter; a no-break space to a space; a
 * curly apostrophe to a straight one. Invisible characters are written as escapes.
 *
 * The same 29 pairs live in the portal (engineering/portal/worker.js, adminListLeads,
 * NAME_FOLD_PAIRS) as a second copy: one home under engineering/contract/ was weighed and not taken
 * (#879 s01 F24), so it is reconcile work. Change both or neither.
 */
export const NAME_FOLD_PAIRS: ReadonlyArray<readonly [string, string]> = [
  ["à", "a"], ["á", "a"], ["â", "a"], ["ã", "a"], ["ä", "a"], ["å", "a"], ["ç", "c"],
  ["è", "e"], ["é", "e"], ["ê", "e"], ["ë", "e"], ["ì", "i"], ["í", "i"], ["î", "i"], ["ï", "i"], ["ñ", "n"],
  ["ò", "o"], ["ó", "o"], ["ô", "o"], ["õ", "o"], ["ö", "o"], ["ù", "u"], ["ú", "u"], ["û", "u"], ["ü", "u"],
  ["ý", "y"], ["ÿ", "y"], [" ", " "], ["’", "'"],
];

/** Lowercase `value`, then apply every NAME_FOLD_PAIRS pair — the comparison form of a name. */
export function foldName(value: string): string {
  return NAME_FOLD_PAIRS.reduce((s, [from, to]) => s.split(from).join(to), value.toLowerCase());
}
